import { createContext, useCallback, useContext } from 'react';
import { makeProvider } from '../../helpers/makeContexts';
import { useDataTableStore } from '../DataTableContext';

const initDashDataTable = {
  dataTables: {},
  dtsPassedFromEditor: null,
  isAddData: false,
  dashboardCubeContextApi: null,
  newDashCount: 1,
  tableData: null,
  dataTableCubeMembers: {},
  cubeQuery: {},
  columnHeaders: [],
  anchorUploaded: false, //new context to keep track of uploaded files
  cubeUploadInProgress: false,
  lastUploadMode: null, // 'append' or 'replace'
  fromCreateNewDashboard: false,
  dataTableToUpload: null,
  // Concurrent upload tracking
  activeUploads: {}, // { [dataTableId]: { status, progress, startTime } }
};

function reducer(draft, { type, payload }) {
  switch (type) {
    case 'passToDashModal': {
      draft.dtsPassedFromEditor = payload.dataTableList;
      draft.isAddData = payload.isAddData;
      break;
    }
    case 'setDataTables': {
      draft.dataTables = payload;
      break;
    }
    case 'setDashboardCubeContextApi': {
      draft.dashboardCubeContextApi = payload;
      break;
    }
    case 'addNewDataTable': {
      draft.dataTables[`new${draft.newDashCount}`] = payload;
      draft.newDashCount++;
      break;
    }
    case 'removeDataTable': {
      delete draft.dataTables[payload.id];
      break;
    }
    case 'updateDataTableId': {
      // find the dataTable with the same sample id or account id
      const currentKeys = Object.keys(draft.dataTables);
      currentKeys.forEach((k) => {
        if (
          draft.dataTables[k].SampleDataTableId === payload.SampleDataTableId ||
          (draft.dataTables[k].AccountDataTableId &&
            draft.dataTables[k].AccountDataTableId ===
              payload.AccountDataTableId)
        ) {
          // add the new dataTable with its id
          draft.dataTables[payload.id] = payload;

          // remove the old one
          delete draft.dataTables[k];
        }
      });
      break;
    }
    //update any property
    case 'updateDataTable': {
      if (draft.dataTables[payload.id]) {
        draft.dataTables[payload.id] = payload;
      }
      break;
    }
    case 'setAnchorUploaded': {
      draft.anchorUploaded = payload;
      break;
    }
    case 'setDataTableToUpload': {
      draft.dataTableToUpload = payload;
      break;
    }
    case 'setTableData': {
      draft.tableData = payload;
      break;
    }
    case 'setDataTableCubeMembers': {
      draft.dataTableCubeMembers = payload;
      break;
    }
    case 'setCubeQuery': {
      draft.cubeQuery = payload;
      break;
    }
    case 'setColumnHeaders': {
      draft.columnHeaders = payload;
      break;
    }
    case 'setCubeUploadInProgress': {
      draft.cubeUploadInProgress = payload;
      break;
    }
    case 'setLastUploadMode': {
      draft.lastUploadMode = payload;
      break;
    }
    case 'setFromCreateNewDashboard': {
      draft.fromCreateNewDashboard = payload;
      break;
    }
    // Phase 1: Concurrent upload state management
    case 'setUploadState': {
      // payload: { dataTableId, status, progress, startTime }
      draft.activeUploads[payload.dataTableId] = {
        status: payload.status,
        progress: payload.progress || 0,
        startTime: payload.startTime || Date.now(),
      };
      break;
    }
    case 'clearUploadState': {
      // payload: dataTableId
      delete draft.activeUploads[payload];
      break;
    }
    case 'clearAllUploadStates': {
      draft.activeUploads = {};
      break;
    }
    default: {
      return null;
    }
  }
}

const [Context, Dispatch] = [createContext(null), createContext(null)];

export const DashboardDataTablesProvider = makeProvider(
  Context,
  Dispatch,
  reducer,
  initDashDataTable
);

export function useDashboardDataTables() {
  return useContext(Context);
}

export function useDashboardDataTablesDispatch() {
  return useContext(Dispatch);
}

// a hook for getting the default chart configurations - returns an object with the chartConfig and gridConfig, returns null if there is no default
export function useGetDefaultChartConfig() {
  const { dataTables } = useContext(Context); // the DashboardDataTables for this dashboard
  const { dts } = useDataTableStore(); // the list of DataTables and their info

  return useCallback(() => {
    //search the data tables to find the anchor file
    const anchorDashboardDatatable = Object.values(dataTables).find(
      (dt) => dt.anchorDataTable
    );

    //if no anchor table is found, return null
    if (!anchorDashboardDatatable) {
      return null;
    }

    //get the DataTable from the DataTableContext
    const anchorDataTable = dts[anchorDashboardDatatable.DataTableId];

    // if there is no entry for the anchor table, or if it doesn't have either defaultChartConfig or defaultGridConfig, return null
    if (
      !anchorDataTable?.defaultChartConfig &&
      !anchorDataTable?.defaultGridConfig
    ) {
      return null;
    }

    return {
      chartConfig: anchorDataTable.defaultChartConfig,
      gridConfig: anchorDataTable.defaultGridConfig,
    };
  }, [dataTables, dts]);
}
